
import { SimulationConfig, AppSave } from '@/types';
import { persistentStore } from '@/lib/LocalPersistentStore';
import { apiFetch, smartFetch } from '@/lib/api';
import { computeHash } from '@/lib/hash';
import { smartCache } from '@/lib/cache/SmartCacheManager';

// Assumes backend endpoints:
// GET /api/saves
// POST /api/saves (body: { name, config, isDefault })
// PUT /api/saves/:id (body: { name, isDefault })
// DELETE /api/saves/:id
// POST /api/upload (form-data: file), returns { url: string }

export class CloudService {
  private static API_BASE = '/api';
  private static memCache: Record<string, { data: any, timestamp: number }> = {};
  private static CACHE_TTL = 300000; // 5 minutes

  private static getFromMemCache(key: string) {
    const entry = this.memCache[key];
    if (entry && (Date.now() - entry.timestamp < this.CACHE_TTL)) {
      return entry.data;
    }
    return null;
  }

  private static setMemCache(key: string, data: any) {
    this.memCache[key] = { data, timestamp: Date.now() };
  }

  private static clearMemCache(keyPrefix?: string) {
    if (keyPrefix) {
      Object.keys(this.memCache).forEach(key => {
        if (key.startsWith(keyPrefix)) delete this.memCache[key];
      });
    } else {
      this.memCache = {};
    }
  }

  static async getSaves(): Promise<AppSave[]> {
    // 1. Try Memory cache
    const cached = this.getFromMemCache('saves');
    if (cached) return cached;

    // 2. Try Persistent cache (Phase 6)
    const local = await persistentStore.getJson<AppSave[]>('SAVES', 'cloud_index');
    
    // 3. Background fetch and update
    try {
      const fetchPromise = apiFetch(`${this.API_BASE}/saves`).then(async res => {
        if (!res.ok) return null;
        const data = await res.json();
        if (!Array.isArray(data)) return null;
        const parsedSaves = data.map((s: any) => ({
          ...s,
          config: s.config ? (typeof s.config === 'string' ? JSON.parse(s.config) : s.config) : null
        }));
        this.setMemCache('saves', parsedSaves);
        await persistentStore.setJson('SAVES', 'cloud_index', parsedSaves);
        return parsedSaves;
      });

      if (local) {
        return local;
      }

      const fresh = await fetchPromise;
      return fresh || [];
    } catch (e) {
      console.warn("CloudService: Offline.", e);
      return local || [];
    }
  }

  static async getSave(id: string): Promise<AppSave | null> {
    try {
      const { content: config, manifest } = await smartFetch<SimulationConfig>(
        id,
        'save',
        `${this.API_BASE}/saves/${id}`,
        `${this.API_BASE}/saves/check-version`
      );
      
      const meta = manifest.metadata || {};

      return {
        id: manifest.id,
        name: meta.name || 'Untitled',
        isDefault: !!meta.isDefault,
        updatedAt: typeof manifest.updatedAt === 'string' ? parseInt(manifest.updatedAt, 10) : manifest.updatedAt,
        config
      };
    } catch (e) {
      return null;
    }
  }

  static async saveConfig(name: string, config: SimulationConfig, isDefault: boolean = false, melodyId?: string, soundIds?: string[]): Promise<AppSave | null> {
    try {
      this.clearMemCache('saves');
      this.clearMemCache('storage');
      
      // Strip heavy data from config before sending
      const cleanConfig = { ...config };
      // @ts-ignore
      delete cleanConfig.customMelodies;
      // @ts-ignore
      delete cleanConfig.userSounds;

      const checksum = await computeHash(JSON.stringify(cleanConfig));

      // Collect queued stats to push with the save
      let stats = null;
      try {
          const stored = localStorage.getItem('flush_stats_queue');
          if (stored) {
              stats = JSON.parse(stored);
          }
      } catch (e) {}

      const res = await apiFetch(`${this.API_BASE}/saves`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, config: cleanConfig, isDefault, melodyId, soundIds, stats, checksum })
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      
      // Update local cache
      const manifest = await smartCache.createManifest(saved.id, 1, 'save', cleanConfig, { name: saved.name, isDefault: !!saved.isDefault });
      await smartCache.set(manifest, cleanConfig);

      // Clear stats queue on success
      if (stats) {
          localStorage.removeItem('flush_stats_queue');
      }
      
      // Update local index cache
      const local = await persistentStore.getJson<AppSave[]>('SAVES', 'cloud_index') || [];
      await persistentStore.setJson('SAVES', 'cloud_index', [saved, ...local]);
      
      return saved;
    } catch (e) {
      console.error("Cloud save failed", e);
      return null;
    }
  }

  static async registerMelody(name: string, data: any, size_bytes?: number): Promise<any | null> {
    try {
      this.clearMemCache('melodies');
      this.clearMemCache('storage');
      
      const checksum = await computeHash(typeof data === 'string' ? data : JSON.stringify(data));

      const res = await apiFetch(`${this.API_BASE}/melodies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: data.id, name, data, size_bytes, checksum })
      });
      const saved = await res.json();
      
      // Cache it
      const manifest = await smartCache.createManifest(saved.id, 1, 'melody', data, { name: saved.name });
      await smartCache.set(manifest, data);

      // Update local cache index
      const melodies = await persistentStore.getJson<any[]>('MELODIES', 'cloud_index') || [];
      await persistentStore.setJson('MELODIES', 'cloud_index', [saved, ...melodies]);
      
      return saved;
    } catch (e) {
      return null;
    }
  }

  static async getMelodies(): Promise<any[]> {
    const cached = this.getFromMemCache('melodies');
    if (cached) return cached;

    const local = await persistentStore.getJson<any[]>('MELODIES', 'cloud_index');

    try {
      const res = await apiFetch(`${this.API_BASE}/melodies`);
      if (!res.ok) return local || [];
      const data = await res.json();
      const results = Array.isArray(data) ? data : [];
      this.setMemCache('melodies', results);
      await persistentStore.setJson('MELODIES', 'cloud_index', results);
      return results;
    } catch (e) {
      return local || [];
    }
  }

  static async getMelody(id: string): Promise<any | null> {
    try {
      const { content: data, manifest } = await smartFetch<any>(
        id,
        'melody',
        `${this.API_BASE}/melodies/${id}`,
        `${this.API_BASE}/melodies/check-version`
      );
      
      const meta = manifest.metadata || {};
      
      return {
        id: manifest.id,
        name: meta.name || 'Untitled',
        data
      };
    } catch (e) {
      return null;
    }
  }

  static async updateSave(id: string, updates: { name?: string, isDefault?: boolean, config?: SimulationConfig }): Promise<boolean> {
    try {
      this.clearMemCache('saves');
      const body: any = { ...updates };
      if (body.config) {
          const cleanConfig = { ...body.config };
          // @ts-ignore
          delete cleanConfig.customMelodies;
          // @ts-ignore
          delete cleanConfig.userSounds;
          body.config = cleanConfig;
      }

      const res = await apiFetch(`${this.API_BASE}/saves/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  static async deleteSave(id: string): Promise<boolean> {
    try {
      this.clearMemCache('saves');
      this.clearMemCache('storage');
      const res = await apiFetch(`${this.API_BASE}/saves/${id}`, { 
        method: 'DELETE'
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  // Sounds
  static async getSounds(): Promise<any[]> {
    const cached = this.getFromMemCache('sounds');
    if (cached) return cached;

    try {
      const res = await apiFetch(`${this.API_BASE}/sounds`);
      if (!res.ok) return [];
      const data = await res.json();
      const results = Array.isArray(data) ? data : [];
      this.setMemCache('sounds', results);
      return results;
    } catch (e) {
      return [];
    }
  }

  static async registerSound(name: string, url: string, id?: string, size_bytes?: number): Promise<any | null> {
    try {
      this.clearMemCache('sounds');
      this.clearMemCache('storage');
      const res = await apiFetch(`${this.API_BASE}/sounds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, data: url, id, size_bytes }) // Changed 'url' to 'data' to match backend schema
      });
      if (!res.ok) throw new Error('Sound registration failed');
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  static async registerSoundData(name: string, data: string): Promise<any | null> {
      return this.registerSound(name, data);
  }

  static async deleteSound(id: string): Promise<boolean> {
    try {
      this.clearMemCache('sounds');
      this.clearMemCache('storage');
      const res = await apiFetch(`${this.API_BASE}/sounds/${id}`, { 
        method: 'DELETE'
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  // Songs
  static async getSongs(): Promise<any[]> {
    const cached = this.getFromMemCache('songs');
    if (cached) return cached;

    try {
      const res = await apiFetch(`${this.API_BASE}/songs`);
      if (!res.ok) return [];
      const results = await res.json();
      this.setMemCache('songs', results);
      return results;
    } catch (e) {
      return [];
    }
  }

  static async registerSong(name: string, data: string | any, id?: string, size_bytes?: number): Promise<any | null> {
    try {
      this.clearMemCache('songs');
      this.clearMemCache('storage');
      
      const checksum = await computeHash(typeof data === 'string' ? data : JSON.stringify(data));
      
      const res = await apiFetch(`${this.API_BASE}/songs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name, data, size_bytes, checksum })
      });
      if (!res.ok) throw new Error('Song registration failed');
      const saved = await res.json();
      
      // Cache it
      const manifest = await smartCache.createManifest(saved.id, 1, 'song', data, { name: saved.name });
      await smartCache.set(manifest, data);
      
      return saved;
    } catch (e) {
      return null;
    }
  }

  static async getConvertedSongs(): Promise<any[]> {
    const cached = this.getFromMemCache('converted_songs');
    if (cached) return cached;

    try {
      const res = await apiFetch(`${this.API_BASE}/converted_songs`);
      if (!res.ok) return [];
      const data = await res.json();
      const results = Array.isArray(data) ? data : [];
      this.setMemCache('converted_songs', results);
      return results;
    } catch (e) {
      return [];
    }
  }

  static async registerConvertedSong(name: string, data: string): Promise<any | null> {
    try {
      this.clearMemCache('converted_songs');
      this.clearMemCache('storage');
      const res = await apiFetch(`${this.API_BASE}/converted_songs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, data })
      });
      if (!res.ok) throw new Error('Converted song registration failed');
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  static async deleteSong(id: string): Promise<boolean> {
    try {
      this.clearMemCache('songs');
      this.clearMemCache('storage');
      const res = await apiFetch(`${this.API_BASE}/songs/${id}`, { 
        method: 'DELETE'
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  static async deleteMelody(id: string): Promise<boolean> {
    try {
      this.clearMemCache('melodies');
      this.clearMemCache('storage');
      const res = await apiFetch(`${this.API_BASE}/melodies/${id}`, { 
        method: 'DELETE'
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  static async uploadFile(file: File): Promise<{ url: string, size: number } | null> {
    try {
      this.clearMemCache('storage');
      
      // Step 1: Compute hash for deduplication
      const checksum = await computeHash(file);
      
      // Step 2: Pre-check if file exists
      const checkRes = await apiFetch(`${this.API_BASE}/upload/check?checksum=${checksum}`);
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (checkData.exists) {
          console.log(`CloudService: Deduplication hit for ${file.name}`);
          return { url: checkData.url, size: checkData.size };
        }
      }

      // Step 3: Perform actual upload if not found
      const formData = new FormData();
      formData.append('file', file);
      formData.append('checksum', checksum);

      const res = await apiFetch(`${this.API_BASE}/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (errorData.error) {
          throw new Error(errorData.error);
        }
        throw new Error('Upload failed');
      }

      const data = await res.json();
      return { url: data.url, size: data.size };
    } catch (e: any) {
      console.warn("Cloud upload failed:", e);
      alert(e.message || "Upload failed");
      return null;
    }
  }

  static async getStorageUsage(): Promise<any> {
    const cached = this.getFromMemCache('storage');
    if (cached) return cached;

    try {
      const res = await apiFetch(`${this.API_BASE}/storage/usage`);
      if (!res.ok) return null;
      const data = await res.json();
      this.setMemCache('storage', data);
      return data;
    } catch (e) {
      return null;
    }
  }

  static async flushQueuedStats(): Promise<boolean> {
    try {
      const stored = localStorage.getItem('flush_stats_queue');
      if (!stored) return true;

      const stats = JSON.parse(stored);
      const res = await apiFetch(`${this.API_BASE}/stats/flush`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stats)
      });

      if (res.ok) {
        localStorage.removeItem('flush_stats_queue');
        return true;
      }
      return false;
    } catch (e) {
      console.error('CloudService: Failed to flush stats', e);
      return false;
    }
  }
}
